import type { EmailRepository } from '../application/EmailRepository'
import type {
  EmailSlaDetail,
  EmailSlaSummary,
  EmailStatus,
  EmailThreadDetail,
  EmailThreadSummary,
} from '../domain/email'

const now = '2026-08-15T14:42:00.000Z'
// L'échéance SLA doit rester crédible par rapport à l'horloge réelle du navigateur
// (l'affichage « restantes » se calcule côté client), contrairement au reste des
// dates fixtures qui restent figées à `now`.
const slaReferenceMs = Date.now()

export function createFixtureEmailRepository(): EmailRepository {
  let thread: EmailThreadSummary = {
    id: 'email-thread-fixture-1',
    mailboxId: 'email-mailbox-info',
    mailboxName: 'Info',
    mailboxSlug: 'info',
    subject: 'Demande de déneigement résidentiel',
    status: 'NEW',
    assignedUserId: null,
    assignedUserName: null,
    lastMessageAt: now,
    lastInboundAt: now,
    lastSenderAddress: 'marie@example.com',
    lastSenderName: 'Marie Tremblay',
    lastPreview: 'Bonjour, j’aimerais obtenir une estimation pour mon entrée.',
    attachmentCount: 1,
    unread: true,
    version: 1,
    sla: null,
  }
  let sla: EmailSlaDetail | null = {
    id: 'fixture-sla-1',
    status: 'RUNNING',
    dueAt: new Date(slaReferenceMs + 25 * 60 * 1000).toISOString(),
    respondedAt: null,
    businessMinutesToResponse: null,
    noResponseRequired: false,
    noResponseReason: null,
    targetMinutes: 60,
    urgency: 'ATTENTION',
  }
  function slaSummary(): EmailSlaSummary | null {
    if (!sla) return null
    return { id: sla.id, status: sla.status, dueAt: sla.dueAt, breachedAt: null, urgency: sla.urgency }
  }
  thread.sla = slaSummary()
  const candidates = [
    { userId: '00000000-0000-4000-b000-000000000001', displayName: 'Jean-François Roy' },
    { userId: '00000000-0000-4000-b000-000000000002', displayName: 'Camille Gagnon' },
  ]
  const fixtureOwnerId = '00000000-0000-4000-b000-000000000001'
  let settleOnSend = false
  const detail = (): EmailThreadDetail => ({
    thread: {
      id: thread.id,
      mailboxId: thread.mailboxId,
      mailboxName: thread.mailboxName,
      mailboxSlug: thread.mailboxSlug,
      subject: thread.subject,
      status: thread.status,
      assignedUserId: thread.assignedUserId,
      assignedUserName: thread.assignedUserName,
      lastMessageAt: thread.lastMessageAt,
      version: thread.version,
      canAssign: true,
      canSend: true,
      sla,
    },
    messages: [
      {
        id: 'fixture-message-1',
        direction: 'INBOUND',
        fromAddress: thread.lastSenderAddress,
        fromName: thread.lastSenderName,
        subject: thread.subject,
        htmlBody:
          '<p>Bonjour,</p><p>J’aimerais obtenir une estimation pour le déneigement de mon entrée cet hiver.</p><img src="https://example.com/tracker.gif"><p>Merci!</p>',
        textBody:
          'Bonjour, j’aimerais obtenir une estimation pour le déneigement de mon entrée cet hiver. Merci!',
        receivedAt: now,
        sentAt: null,
        deliveredAt: null,
        sendStatus: null,
        recipients: [{ type: 'TO', address: 'info@groupereca.com', displayName: null }],
        attachments: [
          {
            id: 'fixture-attachment-1',
            filename: 'entree.jpg',
            mimeType: 'image/jpeg',
            sizeBytes: 248000,
            isInline: false,
          },
        ],
      },
    ],
    assignmentCandidates: candidates,
    assignmentHistory: [],
  })
  function update(status: EmailStatus, assignedUserId = thread.assignedUserId) {
    thread = {
      ...thread,
      status,
      assignedUserId,
      assignedUserName: candidates.find((item) => item.userId === assignedUserId)?.displayName ?? null,
      version: thread.version + 1,
      sla: slaSummary(),
    }
    return Promise.resolve({ ...thread })
  }
  return {
    listMailboxes: () =>
      Promise.resolve([
        {
          id: 'email-mailbox-info',
          name: 'Info',
          slug: 'info',
          type: 'shared',
          address: 'info@groupereca.com',
          canAssign: true,
          canSend: true,
          unreadCount: thread.unread ? 1 : 0,
        },
      ]),
    listInbox: (filters) => {
      const query = filters.search?.toLowerCase()
      const inScope =
        !query ||
        [thread.subject, thread.lastSenderAddress, thread.lastSenderName ?? '', thread.lastPreview].some(
          (value) => value.toLowerCase().includes(query),
        )
      const toReply = thread.status === 'NEW' || thread.status === 'IN_PROGRESS'
      const settled = thread.status === 'DONE' || thread.status === 'ARCHIVED'
      const inView =
        filters.view === 'SENT'
          ? false
          : filters.view === 'SEARCH'
            ? true
            : filters.view === 'ARCHIVED'
              ? thread.status === 'ARCHIVED'
              : filters.view === 'SETTLED'
                ? settled
                : filters.view === 'TO_DO'
                  ? toReply
                  : filters.view === 'UNREAD'
                    ? thread.unread && thread.status !== 'ARCHIVED'
                    : filters.view === 'SLA_PENDING'
                      ? sla?.status === 'RUNNING'
                      : filters.view === 'SLA_OVERDUE'
                        ? sla?.status === 'BREACHED'
                        : thread.status !== 'ARCHIVED'
      const visible = inScope && inView && (!filters.status || thread.status === filters.status)
      return Promise.resolve({
        items: visible ? [{ ...thread }] : [],
        page: { limit: 50, offset: 0, total: visible ? 1 : 0, hasMore: false },
        counts: {
          toReply: inScope && toReply ? 1 : 0,
          toReplyUnread: inScope && toReply && thread.unread ? 1 : 0,
          waiting: inScope && thread.status === 'WAITING_CUSTOMER' ? 1 : 0,
          settled: inScope && settled ? 1 : 0,
        },
      })
    },
    getThread: () => Promise.resolve(detail()),
    getComposeOptions: () =>
      Promise.resolve([
        {
          mailboxId: 'email-mailbox-info',
          mailboxName: 'Info',
          addresses: [{ id: 'fixture-address-info', address: 'info@groupereca.com', isPrimary: true }],
          signatures: [],
        },
      ]),
    listDrafts: () => Promise.resolve([]),
    saveDraft: (command) =>
      Promise.resolve({ id: command.id, version: (command.expectedVersion ?? 0) + 1, updatedAt: now }),
    deleteDraft: () => Promise.resolve(),
    addDraftAttachment: (_draftId, file) =>
      Promise.resolve({
        id: crypto.randomUUID(),
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        storagePath: `fixture/${file.name}`,
      }),
    removeDraftAttachment: () => Promise.resolve(),
    setDraftSettle: (_draftId, settle) => {
      settleOnSend = settle
      return Promise.resolve()
    },
    sendDraft: () => {
      thread = { ...thread, status: settleOnSend ? 'DONE' : 'WAITING_CUSTOMER', version: thread.version + 1 }
      return Promise.resolve({
        messageId: 'fixture-sent',
        providerEmailId: 'fixture-provider',
        status: 'SENT',
      })
    },
    listThreadViewers: () =>
      Promise.resolve([
        { userId: candidates[1]?.userId ?? '', displayName: 'Camille Gagnon', lastReadAt: now },
      ]),
    markRead: () => {
      thread = { ...thread, unread: false }
      return Promise.resolve()
    },
    claim: () => update('IN_PROGRESS', fixtureOwnerId),
    reassign: (command) => update(thread.status, command.targetUserId),
    unassign: () => update(thread.status, null),
    changeStatus: (command) => update(command.status),
    waiveSla: (command) => {
      if (sla) {
        sla = {
          ...sla,
          status: 'WAIVED',
          noResponseRequired: true,
          noResponseReason: command.reason,
          urgency: 'WAIVED',
        }
        thread = { ...thread, sla: slaSummary() }
      }
      return Promise.resolve()
    },
    createAttachmentUrl: () => Promise.resolve('data:text/plain,fixture'),
  }
}
